import React from 'react';

/**
 * Minimal ZIP creation utilities (store method, no compression).
 * Produces a valid ZIP file that can be downloaded.
 */
function makeZip(files) {
  // files: [{ name: string, content: string }]

  const encoder = new TextEncoder();
  const encodedFiles = files.map(f => ({
    name: f.name,
    bytes: encoder.encode(f.content)
  }));

  // Build local file headers + data
  const parts = [];
  const centralDirEntries = [];
  let offset = 0;

  for (const f of encodedFiles) {
    const nameBytes = encoder.encode(f.name);
    const crc = crc32(f.bytes);

    // Local file header
    const localHeader = new Uint8Array(30 + nameBytes.length);
    const lhView = new DataView(localHeader.buffer);
    lhView.setUint32(0, 0x04034b50, true);  // signature
    lhView.setUint16(4, 20, true);           // version needed
    lhView.setUint16(6, 0, true);            // flags
    lhView.setUint16(8, 0, true);            // compression: store
    lhView.setUint16(10, 0, true);           // mod time
    lhView.setUint16(12, 0, true);           // mod date
    lhView.setUint32(14, crc, true);         // crc-32
    lhView.setUint32(18, f.bytes.length, true); // compressed size
    lhView.setUint32(22, f.bytes.length, true); // uncompressed size
    lhView.setUint16(26, nameBytes.length, true); // filename length
    lhView.setUint16(28, 0, true);           // extra field length
    localHeader.set(nameBytes, 30);

    parts.push(localHeader);
    parts.push(f.bytes);

    // Central directory entry
    const cdEntry = new Uint8Array(46 + nameBytes.length);
    const cdView = new DataView(cdEntry.buffer);
    cdView.setUint32(0, 0x02014b50, true);   // signature
    cdView.setUint16(4, 20, true);            // version made by
    cdView.setUint16(6, 20, true);            // version needed
    cdView.setUint16(8, 0, true);             // flags
    cdView.setUint16(10, 0, true);            // compression
    cdView.setUint16(12, 0, true);            // mod time
    cdView.setUint16(14, 0, true);            // mod date
    cdView.setUint32(16, crc, true);          // crc-32
    cdView.setUint32(20, f.bytes.length, true); // compressed size
    cdView.setUint32(24, f.bytes.length, true); // uncompressed size
    cdView.setUint16(28, nameBytes.length, true); // filename length
    cdView.setUint16(30, 0, true);            // extra field length
    cdView.setUint16(32, 0, true);            // comment length
    cdView.setUint16(34, 0, true);            // disk start
    cdView.setUint16(36, 0, true);            // internal attrs
    cdView.setUint32(38, 0, true);            // external attrs
    cdView.setUint32(42, offset, true);       // local header offset
    cdEntry.set(nameBytes, 46);

    centralDirEntries.push(cdEntry);
    offset += localHeader.length + f.bytes.length;
  }

  // End of central directory record
  const cdOffset = offset;
  const cdSize = centralDirEntries.reduce((s, e) => s + e.length, 0);
  const eocd = new Uint8Array(22);
  const eocdView = new DataView(eocd.buffer);
  eocdView.setUint32(0, 0x06054b50, true);    // signature
  eocdView.setUint16(4, 0, true);             // disk number
  eocdView.setUint16(6, 0, true);             // disk with cd
  eocdView.setUint16(8, encodedFiles.length, true); // entries on disk
  eocdView.setUint16(10, encodedFiles.length, true); // total entries
  eocdView.setUint32(12, cdSize, true);       // cd size
  eocdView.setUint32(16, cdOffset, true);     // cd offset
  eocdView.setUint16(20, 0, true);            // comment length

  // Concatenate
  const totalSize = offset + cdSize + 22;
  const result = new Uint8Array(totalSize);
  let pos = 0;
  for (const p of parts) {
    result.set(p, pos);
    pos += p.length;
  }
  for (const cd of centralDirEntries) {
    result.set(cd, pos);
    pos += cd.length;
  }
  result.set(eocd, pos);

  return new Blob([result], { type: 'application/zip' });
}

/* CRC-32 lookup table */
const crcTable = [];
for (let n = 0; n < 256; n++) {
  let c = n;
  for (let k = 0; k < 8; k++) {
    c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
  }
  crcTable[n] = c;
}

function crc32(bytes) {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) {
    crc = crcTable[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}


export default class extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      prospects: [],
      loading: true,
      error: null,
      zipReady: false,
      copySuccess: null,
    };
  }

  componentDidMount() {
    document.title = "Pagina Generator (CTO) - Ghostin";
    this.loadTargets();
  }

  loadTargets = () => {
    this.setState({ loading: true, error: null });
    fetch('/Targets.csv?_=' + Math.random().toString(36).slice(2), {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Targets.csv niet gevonden in de public directory. ' +
            'Plaats het bestand in /public/Targets.csv');
        }
        return response.text();
      })
      .then(csvText => {
        const prospects = this.parseCSV(csvText);
        this.setState({ prospects, loading: false });
      })
      .catch(err => {
        this.setState({ error: err.message, loading: false });
      });
  }

  parseCSV(csvText) {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = this.parseCSVLine(lines[0]);
    const naamIdx = headers.findIndex(h => h.trim() === 'Naam');
    const bedrijfIdx = headers.findIndex(h => h.trim() === 'Bedrijf');
    const concurrentenIdx = headers.findIndex(h => h.trim() === 'Concurrenten');

    const prospects = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = this.parseCSVLine(lines[i]);
      const fullname = cols[naamIdx] ? cols[naamIdx].trim() : '';
      const companyname = cols[bedrijfIdx] ? cols[bedrijfIdx].trim() : '';
      const competition = cols[concurrentenIdx] ? cols[concurrentenIdx].trim() : '';

      if (!fullname || !companyname) continue;

      const firstname = fullname.split(' ')[0];
      const fileSlug = fullname
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
      const companySlug = companyname
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

      prospects.push({
        fullname,
        firstname,
        companyname,
        competition,
        fileSlug,
        companySlug,
      });
    }
    return prospects;
  }

  parseCSVLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result;
  }

  generateFileContent(prospect) {
    const { fullname, firstname, companyname, competition, fileSlug, companySlug } = prospect;
    const compProp = competition
      ? `\n      competition="${competition}"`
      : '';

    return `import React from 'react'

import LetterLandingCTO from '../LetterLandingCTO';

import logo_target from '../../../images/letterfunnel/${companySlug}_logo.webp';
import screenshot_target from '../../../images/letterfunnel/${fileSlug}.webp';


export default class extends React.Component {

  // variable "competition" is optional, fallback: "jouw B2B concurrenten".
  render() {
    return <LetterLandingCTO
      fullname="${fullname}"
      firstname="${firstname}"
      companyname="${companyname}"${compProp}
      logo_target={logo_target}
      screenshot_target={screenshot_target}
    />;
  }

}
`;
  }

  downloadZip = () => {
    const { prospects } = this.state;
    const files = prospects.map(p => ({
      name: p.fileSlug + '.js',
      content: this.generateFileContent(p),
    }));

    const blob = makeZip(files);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'landing-pages-cto.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copySuccess: label });
      setTimeout(() => this.setState({ copySuccess: null }), 2000);
    });
  }

  copyImports = () => {
    const { prospects } = this.state;
    const lines = prospects.map(p =>
      `import ${p.fileSlug} from './letterfunnel/landings/${p.fileSlug}.js';`
    );
    this.copyToClipboard(lines.join('\n'), 'imports');
  }

  copyRoutes = () => {
    const { prospects } = this.state;
    const lines = prospects.map(p =>
      `          <StartRoute exact path="/${p.fileSlug}" component={${p.fileSlug}} hideheader="true" />`
    );
    this.copyToClipboard(lines.join('\n'), 'routes');
  }

  render() {
    const { prospects, loading, error, copySuccess } = this.state;

    return (
      <div>
        <div className="full big black">
          <div className="wrap content-block">

            <h1 style={{ marginBottom: '1em' }}>Pagina Generator (CTO)</h1>

            {loading && (
              <p style={{ color: '#ccc' }}>Targets.csv laden...</p>
            )}

            {error && (
              <div style={{
                background: '#ff4444', color: '#fff',
                padding: '16px 24px', borderRadius: '8px',
                marginBottom: '2em', fontSize: '1em',
              }}>
                <strong>Fout:</strong> {error}
              </div>
            )}

            {!loading && !error && prospects.length === 0 && (
              <p style={{ color: '#ffaa00' }}>
                Geen prospects gevonden in Targets.csv. Controleer het bestandsformaat.
              </p>
            )}

            {!loading && !error && prospects.length > 0 && (
              <React.Fragment>
                <p style={{ color: '#000', marginBottom: '1.5em' }}>
                  <strong>{prospects.length}</strong> prospects gevonden in Targets.csv.
                </p>

                <div style={{ marginBottom: '2em', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={this.downloadZip}
                    className="btn btn-fat btn-if btn-if-fill-gold"
                    style={{ fontSize: '1.1em', padding: '12px 32px' }}
                  >
                    Download ZIP ({prospects.length} pagina's)
                  </button>
                  <button
                    onClick={this.copyImports}
                    className="btn btn-fat btn-if btn-if-fill-gold"
                    style={{ fontSize: '1.1em', padding: '12px 32px' }}
                  >
                    {copySuccess === 'imports' ? 'Gekopieerd!' : `Kopieer imports (${prospects.length})`}
                  </button>
                  <button
                    onClick={this.copyRoutes}
                    className="btn btn-fat btn-if btn-if-fill-gold"
                    style={{ fontSize: '1.1em', padding: '12px 32px' }}
                  >
                    {copySuccess === 'routes' ? 'Gekopieerd!' : `Kopieer routes (${prospects.length})`}
                  </button>
                </div>

                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                  color: '#000', fontSize: '0.9em',
                }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #444' }}>
                      <th style={{ padding: '8px' }}>#</th>
                      <th style={{ padding: '8px' }}>Naam</th>
                      <th style={{ padding: '8px' }}>Bedrijf</th>
                      <th style={{ padding: '8px' }}>Concurrenten</th>
                      <th style={{ padding: '8px' }}>Bestand</th>
                      <th style={{ padding: '8px' }}>Route</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prospects.map((p, idx) => (
                      <tr key={p.fileSlug} style={{ borderBottom: '1px solid #333' }}>
                        <td style={{ padding: '8px', color: '#000' }}>{idx + 1}</td>
                        <td style={{ padding: '8px' }}>{p.fullname}</td>
                        <td style={{ padding: '8px' }}>{p.companyname}</td>
                        <td style={{ padding: '8px', color: '#000' }}>
                          {p.competition || '—'}
                        </td>
                        <td style={{ padding: '8px', fontFamily: 'monospace', color: '#06c' }}>
                          {p.fileSlug}.js
                        </td>
                        <td style={{ padding: '8px', fontFamily: 'monospace' }}>
                          <a href={`/${p.fileSlug}`} target="_blank" rel="noopener noreferrer" style={{ color: '#06c' }}>
                            /{p.fileSlug}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </React.Fragment>
            )}

          </div>
        </div>
      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./src/containers/PageGeneratorCTO.js